import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as productService from '../lib/services/productService';
import Logo from '../components/Logo';
import { useCart } from '../context/CartContext';
import { useCustomerType } from '../context/CustomerTypeContext';
import { richTextToPlainText } from '../lib/richText';
import './Products.css';

const PAGE_SIZE = 15;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productImageIndices, setProductImageIndices] = useState({});
  const { addToCart, cartItems } = useCart();
  const { isPrivate } = useCustomerType();
  const location = useLocation();

  useEffect(() => {
    fetchProducts();
    
    // Check if category was passed from navigation
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
    }
  }, [location.state?.selectedCategory]);

  useEffect(() => {
    let filtered = products;

    // Filter by category
    if (selectedCategory === 'new') {
      filtered = filtered.filter((p) => isProductNew(p.created_at));
    } else if (selectedCategory === 'sale') {
      filtered = filtered.filter((p) => p.discount_percent > 0 || (p.discounted_price && p.discounted_price < p.price));
    } else if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((p) => 
        p.name.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower)) ||
        (p.category && p.category.toLowerCase().includes(searchLower)) ||
        (p.ingredients && richTextToPlainText(p.ingredients).toLowerCase().includes(searchLower))
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [selectedCategory, searchTerm, products]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [filteredProducts]);

  const getProductImageIndex = (productId) => productImageIndices[productId] || 0;

  const prevProductImage = (e, productId, length) => {
    e.preventDefault();
    e.stopPropagation();
    setProductImageIndices((prev) => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + length) % length,
    }));
  };

  const nextProductImage = (e, productId, length) => {
    e.preventDefault();
    e.stopPropagation();
    setProductImageIndices((prev) => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % length,
    }));
  };

  const fetchProducts = async () => {
    try {
      const products = await productService.getProducts();
      setProducts(products);
      setFilteredProducts(products);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(products.map((p) => p.category))];
      setCategories(uniqueCategories);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
  };

  const getProductQuantityInCart = (productId) => {
    const cartItem = cartItems.find(item => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  const getProductImages = (product) => {
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim());
    }

    if (product.image_url) {
      return [product.image_url];
    }

    return [];
  };

  const isProductNew = (createdAt) => {
    if (!createdAt) return false;
    const productDate = new Date(createdAt);
    const currentDate = new Date();
    const twoMonthsAgo = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
    return productDate > twoMonthsAgo;
  };

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const pageProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="products-page">
      <section className="products-hero">
        <div className="container">
          <div className="hero-brand">
            <div className="brand-logo-text">
              <Logo size="medium" useImage={true} imageSrc="/logo.png" />
              <h1 className="brand-name-large">MunchUps</h1>
            </div>
            <div className="brand-tagline">MUNCH, CRUNCH, LOVE THE PUNCH</div>
          </div>
          <h1>Our Products</h1>
          <p>Discover our wide range of delicious and healthy snacks</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="products-layout">
            {/* Category Sidebar */}
            <aside className="category-sidebar">
              <h3>Categories</h3>
              <div className="category-list">
                <button
                  className={`category-item ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('all')}
                >
                  All Products
                </button>
                <button
                  className={`category-item ${selectedCategory === 'new' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('new')}
                >
                  ⭐ New
                </button>
                <button
                  className={`category-item ${selectedCategory === 'sale' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('sale')}
                >
                  🔥 Sale
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`category-item ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </aside>

            {/* Products Grid */}
            <div className="products-content">
              {/* Search Bar */}
              <div className="search-bar" style={{ marginBottom: '20px', position: 'relative' }}>
                <input
                  type="text"
                  placeholder="🔍 Search products by name, description, category, or ingredients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    fontSize: '16px',
                    border: '2px solid var(--border-color)',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.3s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '20px',
                      color: '#999',
                      padding: '5px 10px'
                    }}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="no-products">
                  <p>{searchTerm ? `No products found matching "${searchTerm}".` : 'No products found in this category.'}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-3">
                    {pageProducts.map((product) => {
                      const productImages = getProductImages(product);
                      const imgIndex = getProductImageIndex(product.id);
                      const currentImage = productImages.length > 0 ? productImages[imgIndex] : null;

                      return (
                    <div key={product.id} className="card product-card">
                      <div className="product-image">
                        {currentImage ? (
                          <img src={currentImage} alt={product.name} />
                        ) : (
                          <div className="image-placeholder" aria-label="No image available">No Image</div>
                        )}
                        {productImages.length > 1 && (
                          <>
                            <button
                              className="image-nav-btn image-nav-prev"
                              onClick={(e) => prevProductImage(e, product.id, productImages.length)}
                              aria-label="Previous image"
                            >&#8249;</button>
                            <button
                              className="image-nav-btn image-nav-next"
                              onClick={(e) => nextProductImage(e, product.id, productImages.length)}
                              aria-label="Next image"
                            >&#8250;</button>
                            <div className="slideshow-indicator">
                              {productImages.map((_, imageIndex) => (
                                <span
                                  key={`${product.id}-slide-${imageIndex}`}
                                  className={`slideshow-dot ${imgIndex === imageIndex ? 'active' : ''}`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                        {isProductNew(product.created_at) && (
                          <span className="new-badge">⭐ New</span>
                        )}
                        {product.gluten_free && (
                          <span className="gluten-free-tag">Gluten Free</span>
                        )}
                        {(() => {
                          const available = product.stock_quantity - (product.defective_count || 0);
                          if (available <= 0) {
                            return <div className="low-stock-ribbon">Out of stock</div>;
                          }
                          if (available > 0 && available < 20) {
                            return <div className="low-stock-ribbon">Very few left</div>;
                          }
                          return null;
                        })()}
                      </div>
                      <div className="product-info">
                        <h3>{product.name}</h3>
                        {product.description && (
                          <p className="product-description">{product.description.substring(0, 50)}...</p>
                        )}
                        {product.weight && product.weight > 0 && (
                          <p className="product-weight" style={{ fontSize: '0.9em', color: '#666', margin: '5px 0' }}>📦 Weight: {product.weight}g</p>
                        )}
                        {isPrivate && (
                          <>
                            {product.discounted_price && product.discounted_price < product.price ? (
                              <div className="price-section">
                                <p className="product-price">
                                  <span className="old-price">kr {Number(product.price).toFixed(2)} incl. VAT</span>
                                  <span className="new-price">kr {Number(product.discounted_price).toFixed(2)} incl. VAT</span>
                                </p>
                              </div>
                            ) : (
                              <div className="price-section">
                                <p className="product-price">kr {Number(product.price).toFixed(2)} incl. VAT</p>
                              </div>
                            )}
                          </>
                        )}
                        <div className="product-card-actions">
                          <Link
                            to={`/products/${product.id}`}
                            className="btn btn-primary"
                            style={{ flex: 1 }}
                          >
                            View Details
                          </Link>
                          {isPrivate && (
                            <button
                              onClick={() => handleAddToCart(product)}
                              className="btn btn-add-to-cart"
                              title={
                                (product.stock_quantity - (product.defective_count || 0)) <= 0
                                  ? "Out of Stock"
                                  : "Add to Cart"
                              }
                              disabled={(product.stock_quantity - (product.defective_count || 0)) <= 0}
                            >
                              🛒
                              {getProductQuantityInCart(product.id) > 0 && (
                                <span className="cart-quantity-badge">
                                  {getProductQuantityInCart(product.id)}
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="page-btn"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      <span className="page-indicator">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        className="page-btn"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;